import { Button, Col, Divider, Row } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ZoneTypeContext from "../../../../context/wms/zone-type/context";
import ZoneTypeContextProvider from "../../../../context/wms/zone-type/provider";
import { ICreateZoneType, IUpdateZoneType } from "../../../../models/wms/zone-type/request";
import EndPoints from "../../../../services/end-points";
import { execute } from "../../../../utils/helpers/execute";
import { languages } from "../../../../utils/helpers/constants";
import { getTranslations } from "../../../../utils/helpers/translations";
import FieldBuilder from "../../../form-components/field-builder";
import Spin from "../../../general/antd/spin";
import styles from "./style.module.scss";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(ZoneTypeContext);
  const { id } = useParams();

  const methods = useForm<ICreateZoneType>({
    defaultValues: {
      name: {},
      isActive: false,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const onSubmit = async (data: ICreateZoneType) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.wmsLayout.updateZoneType(details.id, data);
        } else {
          await EndPoints.wmsLayout.createZoneType(data);
        }
        navigate("/wms/zone-types");
      },
      fallback: (error) => { },
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    if (details) {
      reset({
        name: getTranslations(details?.translations?.name) ?? {},
        isActive: details.isActive ?? false,
      });
    }
  }, [details]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_zone_type") : t("add_zone_type")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Row gutter={[16, 16]}>
                {languages.map((lang) => (
                  <Col key={lang.code} xs={24} md={12}>
                    <FieldBuilder
                      name={`name[${lang.code}]`}
                      label={`${t("zone_type_name")} (${lang.name})`}
                      input={{ type: "text" }}
                      rules={{ required: true }}
                    />
                  </Col>
                ))}
                <Col xs={24} md={12}>
                  <FieldBuilder
                    name="isActive"
                    label={t("is_active")}
                    input={{ type: "switch" }}
                    rules={{ required: false }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const ZoneTypeForm = () => {
  return (
    <ZoneTypeContextProvider>
      <Form />
    </ZoneTypeContextProvider>
  );
};

export default ZoneTypeForm;
